import { forwardRef, type Ref, type ReactNode, type ComponentPropsWithoutRef } from "react";
import classnames from "classnames";

import { useId } from "util/html";
import { useA11y } from "common/accessibility";
import Icon from "common/core/icon";
import { useAriaErrorDescribedId, type AriaRequired } from "common/core/form/error";
import { Heading } from "common/core/typography/index";

import type { OptionGroupProps, PresentNode } from "..";
import Styles from "./index.module.scss";
import LayoutStyles from "../../layout/index.module.scss";

export type CheckboxProps = AriaRequired<Omit<ComponentPropsWithoutRef<"input">, "type">>;
export type CheckboxBarProps = {
  checkbox: PresentNode;
  label: PresentNode;
  icon?: string;
  subLabel?: string | ReactNode;
  className?: string;
  hasError?: boolean;
  disabled?: boolean;
};

export function CheckboxWrapper({ children }: { children: ReactNode }) {
  return <div className={Styles.checkboxContainer}>{children}</div>;
}

function Checkbox(props: CheckboxProps, ref: Ref<HTMLInputElement>) {
  const { name, "aria-describedby": ariaDescribedBy, ...otherProps } = props;
  const _ariaDescribedBy = useAriaErrorDescribedId({ name, "aria-describedby": ariaDescribedBy });
  return (
    <CheckboxWrapper>
      <input
        {...otherProps}
        name={name}
        aria-describedby={_ariaDescribedBy}
        type="checkbox"
        ref={ref}
      />
      <Icon name="tick-small" />
    </CheckboxWrapper>
  );
}

const CheckboxWithRef = forwardRef(Checkbox);

export function CheckboxLabel({
  checkbox,
  label,
  subLabel,
  disabled,
}: {
  checkbox: PresentNode;
  label: PresentNode;
  subLabel?: PresentNode;
  disabled?: boolean;
}) {
  return (
    <label className={classnames(Styles.optionLabel, subLabel && Styles.optionWithSubLabel)}>
      {checkbox}
      <div className={Styles.optionText}>
        <span className={classnames(Styles.optionLabelText, disabled && Styles.disabled)}>
          {label}
        </span>
        {subLabel && (
          <span className={classnames(Styles.optionSubLabelText, disabled && Styles.disabled)}>
            {subLabel}
          </span>
        )}
      </div>
    </label>
  );
}

export function CheckboxBar({
  checkbox,
  label,
  icon,
  subLabel,
  className,
  hasError,
  disabled,
}: CheckboxBarProps) {
  return (
    <label className={classnames(Styles.checkboxBar, className, hasError && Styles.errorBorder)}>
      <div className={Styles.checkboxBarLabel}>
        {icon && <Icon className={Styles.checkboxBarIcon} name={icon} />}
        <div className={Styles.checkboxBarText}>
          <span className={classnames(Styles.checkboxBarPrimaryLabel, disabled && Styles.disabled)}>
            {label}
          </span>
          {subLabel && (
            <span className={classnames(Styles.checkboxBarSubLabel, disabled && Styles.disabled)}>
              {subLabel}
            </span>
          )}
        </div>
      </div>
      {checkbox}
    </label>
  );
}

export function CheckboxGroup({
  children,
  label,
  groupName,
  groupError,
  "aria-labelledby": ariaLabelledby,
}: OptionGroupProps) {
  // A checkbox group will have one validation error per group.
  const groupLabelId = useId();
  const describedbyId = useA11y().useLabelledOrDescribedBy(
    groupName || groupError?.props.inputName,
  );
  return (
    <fieldset
      aria-labelledby={ariaLabelledby ? ariaLabelledby : groupLabelId}
      aria-describedby={describedbyId}
      className={Styles.checkboxGroup}
    >
      {label && (
        <legend>
          <Heading
            level="h5"
            textStyle="headingFive"
            id={groupLabelId}
            className={LayoutStyles.label}
          >
            {label}
          </Heading>
        </legend>
      )}
      {children}
      {groupError}
    </fieldset>
  );
}

export { CheckboxWithRef as Checkbox };
